/**
 * Main Layout Component
 * Inspired by:
 * - Linear: Dense, keyboard-efficient sidebar navigation
 * - ClickUp: Hierarchical workspace structure
 * - Notion: Clean, minimal aesthetic
 */

import { Link, useLocation } from 'react-router-dom';
import { Fragment, useState } from 'react';
import { Dialog, Transition, Menu } from '@headlessui/react';
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ChartBarIcon,
  SparklesIcon,
  UsersIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  BellIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useWorkspace } from '../context/WorkspaceContext';
import clsx from 'clsx';
import { useEffect } from 'react';

const ICON_MAP: Record<string, any> = {
  HomeIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ChartBarIcon,
  SparklesIcon,
  ShieldCheckIcon,
  UsersIcon,
};

const INITIAL_NAVIGATION = [
  { id: 'dashboard', name: 'Dashboard', href: '/dashboard', icon: 'HomeIcon', visible: true },
  { id: 'tasks', name: 'Tasks', href: '/tasks', icon: 'ClipboardDocumentListIcon', visible: true },
  { id: 'crm', name: 'CRM', href: '/crm', icon: 'UserGroupIcon', visible: true },
  { id: 'notes', name: 'Notes', href: '/notes', icon: 'DocumentTextIcon', visible: true },
  { id: 'insights', name: 'Insights', href: '/insights', icon: 'ChartBarIcon', visible: true },
  { id: 'assistant', name: 'Assistant', href: '/assistant', icon: 'SparklesIcon', visible: true },
  { id: 'build-review', name: 'Build Review', href: '/build-review', icon: 'ShieldCheckIcon', visible: true },
  { id: 'team', name: 'Team', href: '/team', icon: 'UsersIcon', visible: true },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { currentWorkspace } = useWorkspace();
  const [navigation, setNavigation] = useState(INITIAL_NAVIGATION);

  useEffect(() => {
    const loadNav = () => {
      const saved = localStorage.getItem(`sidebar_config_${currentWorkspace?.id || 'default'}`);
      if (saved) {
        setNavigation(JSON.parse(saved));
      } else {
        setNavigation(INITIAL_NAVIGATION);
      }
    };

    loadNav();
    window.addEventListener('sidebar_config_updated', loadNav);
    return () => window.removeEventListener('sidebar_config_updated', loadNav);
  }, [currentWorkspace]);

  const getCurrentPageTitle = () => {
    const nav = navigation.find((n) => n.href === location.pathname);
    return nav?.name || 'Dashboard';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button type="button" className="-m-2.5 p-2.5" onClick={() => setSidebarOpen(false)}>
                      <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>
                <SidebarContent navigation={navigation} />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
        <SidebarContent navigation={navigation} />
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <div className="sticky top-0 z-40 flex h-14 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>

          {/* Separator */}
          <div className="h-6 w-px bg-gray-200 lg:hidden" aria-hidden="true" />

          <div className="flex flex-1 items-center gap-x-4 self-stretch lg:gap-x-6">
            {/* Page title */}
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-semibold text-gray-900">{getCurrentPageTitle()}</h1>
            </div>

            {/* Search */}
            <div className="flex-1 flex items-center justify-end">
              {/* Build Status Indicator */}
              <Link
                to="/build-review"
                className="hidden md:flex items-center gap-2 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 border border-emerald-100 hover:bg-emerald-100 transition-colors mr-4"
              >
                <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span>Build: Passed</span>
              </Link>

              <div className="hidden sm:flex items-center">
                <div className="relative w-64">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search..."
                    className="w-full rounded-lg border border-gray-300 bg-white py-1.5 pl-10 pr-3 text-sm placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border border-gray-200 bg-gray-50 px-1.5 font-mono text-[10px] font-medium text-gray-500">
                    ⌘K
                  </kbd>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-x-3">
              {/* Quick add button */}
              <button className="btn-primary btn-sm hidden sm:flex">
                <PlusIcon className="h-4 w-4" />
                <span>New</span>
              </button>

              {/* Notifications */}
              <button className="btn-icon relative">
                <BellIcon className="h-5 w-5" />
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-[10px] font-medium text-white flex items-center justify-center">
                  3
                </span>
              </button>

              {/* Profile dropdown */}
              <Menu as="div" className="relative">
                <Menu.Button className="flex items-center gap-2 rounded-lg p-1.5 hover:bg-gray-100">
                  <div className="avatar avatar-sm bg-primary-100 text-primary-700">
                    {currentWorkspace?.name?.charAt(0) || 'W'}
                  </div>
                  <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="dropdown-menu right-0 mt-2 w-48">
                    <Menu.Item>
                      {({ active }) => (
                        <Link to="/settings" className={clsx('dropdown-item', active && 'dropdown-item-active')}>
                          <Cog6ToothIcon className="mr-2 h-4 w-4" />
                          Sidebar Settings
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button className={clsx('dropdown-item dropdown-item-danger', active && 'bg-red-50')}>
                          Sign out
                        </button>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="min-h-[calc(100vh-3.5rem)]">
          {children}
        </main>
      </div>
    </div>
  );
}

function SidebarContent({ navigation }: { navigation: any[] }) {
  const location = useLocation();
  const { currentWorkspace } = useWorkspace();

  return (
    <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white border-r border-gray-200 px-4 pb-4">
      {/* Logo */}
      <div className="flex h-14 shrink-0 items-center border-b border-gray-100">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center shadow-sm">
            <span className="text-white font-bold text-sm">AI</span>
          </div>
          <span className="font-semibold text-gray-900">Business AI</span>
        </Link>
      </div>

      {/* Workspace selector */}
      {currentWorkspace && (
        <div className="px-1">
          <Menu as="div" className="relative">
            <Menu.Button className="flex w-full items-center gap-3 rounded-lg p-2 text-left hover:bg-gray-50 transition-colors">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-primary-100 to-primary-200 text-primary-700 font-semibold text-sm">
                {currentWorkspace.name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{currentWorkspace.name}</p>
                <p className="text-xs text-gray-500 capitalize">{currentWorkspace.businessType}</p>
              </div>
              <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            </Menu.Button>
          </Menu>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex flex-1 flex-col">
        <ul className="flex flex-1 flex-col gap-y-1">
          {navigation.filter(item => item.visible).map((item) => {
            const isActive = location.pathname === item.href;
            const Icon = ICON_MAP[item.icon];
            return (
              <li key={item.name}>
                <Link
                  to={item.href}
                  className={clsx(
                    isActive ? 'nav-item-active' : 'nav-item-default'
                  )}
                >
                  {Icon && <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />}
                  {item.name}
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Footer */}
        <div className="mt-auto pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between px-2 py-2">
            <span className="text-xs text-gray-400">Business AI v0.2.0</span>
            <Link to="/settings" className="btn-icon">
              <Cog6ToothIcon className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </nav>
    </div>
  );
}

export default Layout;
